import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const AssetPreviewModal = ({ asset, onClose, onDelete, onDownload }) => {
  if (!asset) return null;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const getFileIcon = (type) => {
    const iconMap = {
      image: 'Image',
      video: 'Video',
      document: 'FileText',
      icon: 'Sparkles'
    };
    return iconMap?.[type] || 'File';
  };

  return (
    <div className="fixed inset-0 bg-background/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground truncate flex-1 mr-4">
            {asset?.name}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-250 flex-shrink-0"
            aria-label="Close preview"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4 md:p-6">
            <div className="lg:col-span-2">
              <div className="bg-muted rounded-lg overflow-hidden aspect-video flex items-center justify-center">
                {asset?.type === 'image' ? (
                  <Image
                    src={asset?.thumbnail}
                    alt={asset?.thumbnailAlt}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-4">
                    <Icon name={getFileIcon(asset?.type)} size={64} className="text-muted-foreground" />
                    <span className="text-lg font-medium text-muted-foreground uppercase">
                      {asset?.format}
                    </span>
                  </div>
                )}
              </div>

              {asset?.usedIn && asset?.usedIn?.length > 0 && (
                <div className="mt-6">
                  <h3 className="caption font-semibold text-foreground mb-3">
                    Used in Projects ({asset?.usedIn?.length})
                  </h3>
                  <div className="space-y-2">
                    {asset?.usedIn?.map((project, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 bg-muted rounded-lg hover:bg-muted/80 transition-all duration-250"
                      >
                        <Icon name="FolderKanban" size={16} className="text-primary" />
                        <span className="caption text-foreground">{project}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="caption font-semibold text-foreground mb-3">Details</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Type</span>
                    <span className="text-sm font-medium text-foreground capitalize">
                      {asset?.type}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Format</span>
                    <span className="text-sm font-medium text-foreground uppercase">
                      {asset?.format}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Size</span>
                    <span className="text-sm font-medium text-foreground">
                      {formatFileSize(asset?.size)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Uploaded</span>
                    <span className="text-sm font-medium text-foreground">
                      {asset?.uploadDate}
                    </span>
                  </div>
                  {asset?.dimensions && (
                    <div className="flex items-center justify-between py-2 border-b border-border">
                      <span className="text-sm text-muted-foreground">Dimensions</span>
                      <span className="text-sm font-medium text-foreground">
                        {asset?.dimensions}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {asset?.tags && asset?.tags?.length > 0 && (
                <div>
                  <h3 className="caption font-semibold text-foreground mb-3">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {asset?.tags?.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-muted rounded-full text-xs font-medium text-foreground"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2 pt-4">
                <Button
                  variant="default"
                  fullWidth
                  iconName="Download"
                  iconPosition="left"
                  onClick={() => onDownload(asset?.id)}
                >
                  Download
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  iconName="Copy"
                  iconPosition="left"
                >
                  Copy URL
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  iconName="Edit"
                  iconPosition="left"
                >
                  Rename
                </Button>
                <Button
                  variant="destructive"
                  fullWidth
                  iconName="Trash2"
                  iconPosition="left"
                  onClick={() => onDelete(asset?.id)}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetPreviewModal;